import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Box,
    Button,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    CircularProgress,
    Alert,
    Snackbar,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    OutlinedInput,
    Switch,
    FormControlLabel,
    Tooltip,
    Collapse
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Close as CloseIcon,
    AccountBalanceWallet as WalletIcon,
    Key as KeyIcon,
    ReceiptLong as LedgerIcon,
    Visibility as PreviewIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';

type MarginType = 'FLAT' | 'PERCENT';

interface Partner {
    id: number;
    name: string;
    defaultMarginType: MarginType;
    defaultMarginValue: number;
    fractionalExtraMarginPercent: number;
    isActive: boolean;
    hostingRates: number[];
    balanceUsd: number;
    customerId: number | null;
    customerName: string | null;
    taxPercent: number;
}

interface CustomerOption {
    id: number;
    customerGroupName: string;
    isActive: boolean;
}

interface PartnerMargin {
    id: number;
    partnerId: number;
    machineModelId: number;
    marginType: MarginType;
    marginValue: number;
    isActive: boolean;
}

interface MachineModel {
    id: number;
    modelName: string;
}

interface LedgerEntry {
    id: number;
    seq: number;
    amountUsd: number;
    transactionType: string;
    currentBalance: number;
    reference: string;
    createdBy: string;
    createdAt: string;
}

interface PartnerPrice {
    hostingCost: number;
    machinePriceUsd: number;
    totalPriceUsd: number;
    fractionalUnitPriceUsd: number | null;
    setupFee: number | null;
    transportFee: number | null;
}

interface PartnerMachinePrice {
    machineModelName: string;
    machineId: number;
    machineHashrate: number;
    hashrateUnit: string;
    prices: PartnerPrice[];
}

interface SnackbarState {
    open: boolean;
    message: string;
    severity: 'success' | 'error';
}

const emptyForm = {
    id: null as number | null,
    name: '',
    defaultMarginType: 'PERCENT' as MarginType,
    defaultMarginValue: '0',
    fractionalExtraMarginPercent: '0',
    isActive: true,
    hostingRates: [] as number[],
    customerId: '' as number | '',
    taxPercent: '5'
};

const PartnerController: React.FC = () => {
    const [partners, setPartners] = useState<Partner[]>([]);
    const [hostingTiers, setHostingTiers] = useState<number[]>([]);
    const [machineModels, setMachineModels] = useState<MachineModel[]>([]);
    const [customers, setCustomers] = useState<CustomerOption[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });

    // partner create/edit
    const [formOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    // one-time api key display
    const [rawApiKey, setRawApiKey] = useState<string | null>(null);
    const [regeneratePartner, setRegeneratePartner] = useState<Partner | null>(null);
    const [regeneratingKey, setRegeneratingKey] = useState(false);

    // top-up
    const [topUpPartner, setTopUpPartner] = useState<Partner | null>(null);
    const [topUpAmount, setTopUpAmount] = useState('');
    const [topUpReference, setTopUpReference] = useState('');

    // ledger
    const [ledgerPartner, setLedgerPartner] = useState<Partner | null>(null);
    const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
    const [ledgerLoading, setLedgerLoading] = useState(false);

    // price preview
    const [previewPartner, setPreviewPartner] = useState<Partner | null>(null);
    const [previewPrices, setPreviewPrices] = useState<PartnerMachinePrice[]>([]);
    const [previewLoading, setPreviewLoading] = useState(false);

    // margins (expandable per partner row)
    const [expandedPartnerId, setExpandedPartnerId] = useState<number | null>(null);
    const [margins, setMargins] = useState<PartnerMargin[]>([]);
    const [marginModelId, setMarginModelId] = useState<string>('');
    const [marginType, setMarginType] = useState<MarginType>('PERCENT');
    const [marginValue, setMarginValue] = useState('');

    const notify = (message: string, severity: 'success' | 'error' = 'success') =>
        setSnackbar({ open: true, message, severity });

    const loadPartners = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_PATH}/admin/partners`, getAxiosConfig());
            setPartners(res.data);
        } catch (e: any) {
            notify(e?.response?.data || 'Failed to load partners', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPartners();
        axios.get(`${API_PATH}/admin/partners/hosting-tiers`, getAxiosConfig())
            .then(res => setHostingTiers(res.data))
            .catch(() => setHostingTiers([]));
        axios.get(`${API_PATH}/machine_model/listAll`, getAxiosConfig())
            .then(res => setMachineModels(res.data))
            .catch(() => setMachineModels([]));
        axios.get(`${API_PATH}/customer/list?orderBy=customer_group_name`, getAxiosConfig())
            .then(res => setCustomers((res.data as CustomerOption[]).filter(c => c.isActive)))
            .catch(() => setCustomers([]));
    }, [loadPartners]);

    // ---------- create / edit ----------

    const openCreate = () => {
        setForm(emptyForm);
        setFormOpen(true);
    };

    const openEdit = (partner: Partner) => {
        setForm({
            id: partner.id,
            name: partner.name,
            defaultMarginType: partner.defaultMarginType,
            defaultMarginValue: String(partner.defaultMarginValue),
            fractionalExtraMarginPercent: String(partner.fractionalExtraMarginPercent),
            isActive: partner.isActive,
            hostingRates: partner.hostingRates || [],
            customerId: partner.customerId ?? '',
            taxPercent: partner.taxPercent != null ? String(partner.taxPercent) : '5'
        });
        setFormOpen(true);
    };

    const savePartner = async () => {
        if (!form.name.trim()) {
            notify('Partner name is required', 'error');
            return;
        }
        const payload = {
            name: form.name.trim(),
            defaultMarginType: form.defaultMarginType,
            defaultMarginValue: parseFloat(form.defaultMarginValue) || 0,
            fractionalExtraMarginPercent: parseFloat(form.fractionalExtraMarginPercent) || 0,
            isActive: form.isActive,
            hostingRates: form.hostingRates,
            // optional: purchases stay blocked until a customer is linked
            customerId: form.customerId === '' ? null : form.customerId,
            taxPercent: form.taxPercent === '' ? null : parseFloat(form.taxPercent)
        };
        try {
            setSaving(true);
            if (form.id === null) {
                const res = await axios.post(`${API_PATH}/admin/partners/provision`, payload, getAxiosConfig());
                setRawApiKey(res.data.rawApiKey);
                notify('Partner provisioned');
            } else {
                await axios.put(`${API_PATH}/admin/partners/${form.id}`, payload, getAxiosConfig());
                notify('Partner updated');
            }
            setFormOpen(false);
            loadPartners();
        } catch (e: any) {
            notify(e?.response?.data || 'Save failed', 'error');
        } finally {
            setSaving(false);
        }
    };

    const regenerateKey = async () => {
        if (!regeneratePartner) return;
        try {
            setRegeneratingKey(true);
            const res = await axios.post(`${API_PATH}/admin/partners/${regeneratePartner.id}/regenerate-key`, {}, getAxiosConfig());
            setRegeneratePartner(null);
            setRawApiKey(res.data.rawApiKey);
            notify('API key regenerated');
        } catch (e: any) {
            notify(e?.response?.data || 'Key regeneration failed', 'error');
        } finally {
            setRegeneratingKey(false);
        }
    };

    // ---------- top-up ----------

    const submitTopUp = async () => {
        if (!topUpPartner) return;
        const amount = parseFloat(topUpAmount);
        if (!amount || amount <= 0) {
            notify('Positive amount required', 'error');
            return;
        }
        try {
            await axios.post(`${API_PATH}/admin/partners/${topUpPartner.id}/top-up`,
                { amountUsd: amount, reference: topUpReference || undefined }, getAxiosConfig());
            notify(`Topped up ${topUpPartner.name} with $${amount}`);
            setTopUpPartner(null);
            setTopUpAmount('');
            setTopUpReference('');
            loadPartners();
        } catch (e: any) {
            notify(e?.response?.data || 'Top-up failed', 'error');
        }
    };

    // ---------- ledger ----------

    const openLedger = async (partner: Partner) => {
        setLedgerPartner(partner);
        setLedgerEntries([]);
        setLedgerLoading(true);
        try {
            const res = await axios.get(`${API_PATH}/admin/partners/${partner.id}/ledger`, getAxiosConfig());
            setLedgerEntries(res.data);
        } catch (e: any) {
            notify('Failed to load ledger', 'error');
        } finally {
            setLedgerLoading(false);
        }
    };

    // ---------- price preview ----------

    const openPreview = async (partner: Partner) => {
        setPreviewPartner(partner);
        setPreviewLoading(true);
        try {
            const res = await axios.get(`${API_PATH}/admin/partners/${partner.id}/price-preview`, getAxiosConfig());
            setPreviewPrices(res.data);
        } catch (e: any) {
            notify('Failed to load price preview', 'error');
            setPreviewPrices([]);
        } finally {
            setPreviewLoading(false);
        }
    };

    // ---------- margins ----------

    const toggleMargins = async (partner: Partner) => {
        if (expandedPartnerId === partner.id) {
            setExpandedPartnerId(null);
            return;
        }
        setExpandedPartnerId(partner.id);
        try {
            const res = await axios.get(`${API_PATH}/admin/partners/${partner.id}/margins`, getAxiosConfig());
            setMargins(res.data);
        } catch {
            setMargins([]);
        }
    };

    const addMargin = async (partnerId: number) => {
        if (!marginModelId || marginValue === '') {
            notify('Model and margin value are required', 'error');
            return;
        }
        try {
            await axios.post(`${API_PATH}/admin/partners/${partnerId}/margins`, {
                machineModelId: parseInt(marginModelId, 10),
                marginType,
                marginValue: parseFloat(marginValue)
            }, getAxiosConfig());
            const res = await axios.get(`${API_PATH}/admin/partners/${partnerId}/margins`, getAxiosConfig());
            setMargins(res.data);
            setMarginModelId('');
            setMarginValue('');
            notify('Margin saved');
        } catch (e: any) {
            notify(e?.response?.data || 'Margin save failed', 'error');
        }
    };

    const deleteMargin = async (partnerId: number, marginId: number) => {
        try {
            await axios.delete(`${API_PATH}/admin/partners/${partnerId}/margins/${marginId}`, getAxiosConfig());
            setMargins(margins.filter(m => m.id !== marginId));
            notify('Margin removed');
        } catch (e: any) {
            notify(e?.response?.data || 'Margin delete failed', 'error');
        }
    };

    const modelName = (id: number) =>
        machineModels.find(m => m.id === id)?.modelName || `Model #${id}`;

    const marginLabel = (type: MarginType, value: number) =>
        type === 'PERCENT' ? `${value}%` : `$${value}`;

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Partners</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                    Add Partner
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell />
                                <TableCell>Name</TableCell>
                                <TableCell>Customer</TableCell>
                                <TableCell>Hosting Rates</TableCell>
                                <TableCell>Default Margin</TableCell>
                                <TableCell>Fractional Extra %</TableCell>
                                <TableCell align="right">Balance (USD)</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {partners.map(partner => (
                                <React.Fragment key={partner.id}>
                                    <TableRow hover>
                                        <TableCell padding="checkbox">
                                            <IconButton size="small" onClick={() => toggleMargins(partner)}>
                                                {expandedPartnerId === partner.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{partner.name}</TableCell>
                                        <TableCell>
                                            {partner.customerName ? (
                                                partner.customerName
                                            ) : (
                                                <Tooltip title="No customer linked — machine purchase is disabled for this partner">
                                                    <Chip label="No customer" color="warning" size="small" />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {(partner.hostingRates || []).map(rate => (
                                                <Chip key={rate} label={rate} size="small" sx={{ mr: 0.5 }} />
                                            ))}
                                        </TableCell>
                                        <TableCell>{marginLabel(partner.defaultMarginType, partner.defaultMarginValue)}</TableCell>
                                        <TableCell>{partner.fractionalExtraMarginPercent}%</TableCell>
                                        <TableCell align="right">
                                            {Number(partner.balanceUsd).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={partner.isActive ? 'Active' : 'Inactive'}
                                                  color={partner.isActive ? 'success' : 'default'} size="small" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => openEdit(partner)}><EditIcon fontSize="small" /></IconButton>
                                            </Tooltip>
                                            <Tooltip title="Top up balance">
                                                <IconButton size="small" onClick={() => setTopUpPartner(partner)}><WalletIcon fontSize="small" /></IconButton>
                                            </Tooltip>
                                            <Tooltip title="Ledger">
                                                <IconButton size="small" onClick={() => openLedger(partner)}><LedgerIcon fontSize="small" /></IconButton>
                                            </Tooltip>
                                            <Tooltip title="Price preview">
                                                <IconButton size="small" onClick={() => openPreview(partner)}><PreviewIcon fontSize="small" /></IconButton>
                                            </Tooltip>
                                            <Tooltip title="Regenerate API key">
                                                <IconButton size="small" onClick={() => setRegeneratePartner(partner)}><KeyIcon fontSize="small" /></IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
                                            <Collapse in={expandedPartnerId === partner.id} unmountOnExit>
                                                <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                        Per-model margin overrides
                                                    </Typography>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Machine Model</TableCell>
                                                                <TableCell>Margin</TableCell>
                                                                <TableCell align="right" />
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {margins.map(margin => (
                                                                <TableRow key={margin.id}>
                                                                    <TableCell>{modelName(margin.machineModelId)}</TableCell>
                                                                    <TableCell>{marginLabel(margin.marginType, margin.marginValue)}</TableCell>
                                                                    <TableCell align="right">
                                                                        <IconButton size="small" onClick={() => deleteMargin(partner.id, margin.id)}>
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                            <TableRow>
                                                                <TableCell>
                                                                    <FormControl size="small" sx={{ minWidth: 220 }}>
                                                                        <InputLabel>Model</InputLabel>
                                                                        <Select value={marginModelId} label="Model"
                                                                                onChange={e => setMarginModelId(String(e.target.value))}>
                                                                            {machineModels.map(model => (
                                                                                <MenuItem key={model.id} value={model.id}>{model.modelName}</MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <FormControl size="small" sx={{ minWidth: 110, mr: 1 }}>
                                                                        <InputLabel>Type</InputLabel>
                                                                        <Select value={marginType} label="Type"
                                                                                onChange={e => setMarginType(e.target.value as MarginType)}>
                                                                            <MenuItem value="PERCENT">Percent</MenuItem>
                                                                            <MenuItem value="FLAT">Flat USD</MenuItem>
                                                                        </Select>
                                                                    </FormControl>
                                                                    <TextField size="small" label="Value" type="number" sx={{ width: 110 }}
                                                                               value={marginValue} onChange={e => setMarginValue(e.target.value)} />
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Button size="small" variant="outlined" onClick={() => addMargin(partner.id)}>
                                                                        Add
                                                                    </Button>
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                            {partners.length === 0 && (
                                <TableRow><TableCell colSpan={9} align="center">No partners configured</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* create / edit dialog */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {form.id === null ? 'Add Partner' : `Edit Partner: ${form.name}`}
                    <IconButton onClick={() => setFormOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Partner name" value={form.name} disabled={form.id !== null}
                                   onChange={e => setForm({ ...form, name: e.target.value })} fullWidth />
                        <FormControl fullWidth>
                            <InputLabel>Customer (invoiced on purchases)</InputLabel>
                            <Select value={form.customerId}
                                    label="Customer (invoiced on purchases)"
                                    onChange={e => setForm({ ...form, customerId: e.target.value === '' ? '' : Number(e.target.value) })}>
                                <MenuItem value="">
                                    <em>None — machine purchase disabled</em>
                                </MenuItem>
                                {customers.map(customer => (
                                    <MenuItem key={customer.id} value={customer.id}>
                                        {customer.customerGroupName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField label="Tax percent on purchases" type="number"
                                   helperText="Applied on partner purchase orders (0-100)"
                                   value={form.taxPercent}
                                   onChange={e => setForm({ ...form, taxPercent: e.target.value })} />
                        <FormControl fullWidth>
                            <InputLabel>Hosting rates</InputLabel>
                            <Select multiple value={form.hostingRates}
                                    onChange={e => setForm({ ...form, hostingRates: e.target.value as number[] })}
                                    input={<OutlinedInput label="Hosting rates" />}
                                    renderValue={selected => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(selected as number[]).map(value => <Chip key={value} label={value} size="small" />)}
                                        </Box>
                                    )}>
                                {hostingTiers.map(tier => (
                                    <MenuItem key={tier} value={tier}>{tier}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl sx={{ minWidth: 140 }}>
                                <InputLabel>Default margin type</InputLabel>
                                <Select value={form.defaultMarginType} label="Default margin type"
                                        onChange={e => setForm({ ...form, defaultMarginType: e.target.value as MarginType })}>
                                    <MenuItem value="PERCENT">Percent</MenuItem>
                                    <MenuItem value="FLAT">Flat USD</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField label="Default margin value" type="number" fullWidth
                                       value={form.defaultMarginValue}
                                       onChange={e => setForm({ ...form, defaultMarginValue: e.target.value })} />
                        </Box>
                        <TextField label="Fractional extra margin (%)" type="number"
                                   helperText="Additional percentage applied on top of the margined per-hash price for fractional miners"
                                   value={form.fractionalExtraMarginPercent}
                                   onChange={e => setForm({ ...form, fractionalExtraMarginPercent: e.target.value })} />
                        <FormControlLabel
                            control={<Switch checked={form.isActive}
                                             onChange={e => setForm({ ...form, isActive: e.target.checked })} />}
                            label="Active" />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFormOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={savePartner} disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* regenerate api key confirmation dialog */}
            <Dialog
                open={regeneratePartner !== null}
                onClose={() => !regeneratingKey && setRegeneratePartner(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Regenerate API key?</DialogTitle>
                <DialogContent>
                    <Alert severity="warning">
                        This will regenerate the existing API key for <strong>{regeneratePartner?.name}</strong>.
                        The current key will stop working immediately.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRegeneratePartner(null)} disabled={regeneratingKey}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="warning" onClick={regenerateKey} disabled={regeneratingKey}>
                        {regeneratingKey ? <CircularProgress size={20} /> : 'Regenerate key'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* one-time api key dialog */}
            <Dialog open={rawApiKey !== null} onClose={() => setRawApiKey(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Partner API Key</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This key is shown only once. Copy it now — it cannot be retrieved later.
                    </Alert>
                    <TextField fullWidth value={rawApiKey || ''} InputProps={{ readOnly: true }}
                               onFocus={e => e.target.select()} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { navigator.clipboard?.writeText(rawApiKey || ''); }}>Copy</Button>
                    <Button variant="contained" onClick={() => setRawApiKey(null)}>Done</Button>
                </DialogActions>
            </Dialog>

            {/* top-up dialog */}
            <Dialog open={topUpPartner !== null} onClose={() => setTopUpPartner(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Top up: {topUpPartner?.name}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Amount (USD)" type="number" value={topUpAmount}
                                   onChange={e => setTopUpAmount(e.target.value)} fullWidth autoFocus />
                        <TextField label="Reference (optional, idempotency key)" value={topUpReference}
                                   onChange={e => setTopUpReference(e.target.value)} fullWidth />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTopUpPartner(null)}>Cancel</Button>
                    <Button variant="contained" onClick={submitTopUp}>Top up</Button>
                </DialogActions>
            </Dialog>

            {/* ledger dialog */}
            <Dialog open={ledgerPartner !== null} onClose={() => setLedgerPartner(null)} maxWidth="md" fullWidth>
                <DialogTitle>Ledger: {ledgerPartner?.name}</DialogTitle>
                <DialogContent>
                    {ledgerLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                    ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Seq</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell align="right">Balance</TableCell>
                                <TableCell>Reference</TableCell>
                                <TableCell>By</TableCell>
                                <TableCell>Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {ledgerEntries.map(entry => (
                                <TableRow key={entry.id}>
                                    <TableCell>{entry.seq}</TableCell>
                                    <TableCell>{entry.transactionType}</TableCell>
                                    <TableCell align="right"
                                               sx={{ color: entry.amountUsd < 0 ? 'error.main' : 'success.main' }}>
                                        {Number(entry.amountUsd).toFixed(2)}
                                    </TableCell>
                                    <TableCell align="right">{Number(entry.currentBalance).toFixed(2)}</TableCell>
                                    <TableCell>{entry.reference}</TableCell>
                                    <TableCell>{entry.createdBy}</TableCell>
                                    <TableCell>{new Date(entry.createdAt).toLocaleString()}</TableCell>
                                </TableRow>
                            ))}
                            {ledgerEntries.length === 0 && (
                                <TableRow><TableCell colSpan={7} align="center">No transactions</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLedgerPartner(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* price preview dialog */}
            <Dialog open={previewPartner !== null} onClose={() => setPreviewPartner(null)} maxWidth="md" fullWidth>
                <DialogTitle>Price preview: {previewPartner?.name}</DialogTitle>
                <DialogContent>
                    {previewLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Model</TableCell>
                                    <TableCell>Hash rate</TableCell>
                                    <TableCell>Hosting rate</TableCell>
                                    <TableCell align="right">Machine price (USD)</TableCell>
                                    <TableCell align="right">Setup fee</TableCell>
                                    <TableCell align="right">Transport</TableCell>
                                    <TableCell align="right">Total (USD)</TableCell>
                                    <TableCell align="right">Fractional price / unit</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {previewPrices.flatMap(machine =>
                                    machine.prices.map((price, index) => (
                                        <TableRow key={`${machine.machineId}-${price.hostingCost}`}>
                                            <TableCell>{index === 0 ? machine.machineModelName : ''}</TableCell>
                                            <TableCell>
                                                {index === 0 ? `${machine.machineHashrate} ${machine.hashrateUnit}` : ''}
                                            </TableCell>
                                            <TableCell>{price.hostingCost}</TableCell>
                                            <TableCell align="right">{price.machinePriceUsd?.toLocaleString()}</TableCell>
                                            <TableCell align="right">{price.setupFee?.toLocaleString() ?? '—'}</TableCell>
                                            <TableCell align="right">{price.transportFee?.toLocaleString() ?? '—'}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                {price.totalPriceUsd?.toLocaleString()}
                                            </TableCell>
                                            <TableCell align="right">
                                                {price.fractionalUnitPriceUsd != null
                                                    ? `$${price.fractionalUnitPriceUsd} / ${machine.hashrateUnit}`
                                                    : '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                                {previewPrices.length === 0 && (
                                    <TableRow><TableCell colSpan={8} align="center">
                                        No prices — check hosting rates and today's prediction batch
                                    </TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewPartner(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={5000}
                      onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {String(snackbar.message)}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PartnerController;
